import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { Layout } from '../../components/layout/Layout';
import { AdminHeader } from '../../components/admin/AdminHeader';
import { AdminNav } from '../../components/admin/AdminNav';
import { AdminLoginCard } from '../../components/admin/AdminLoginCard';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { useProducts } from '../../hooks/useProducts';
import { categories } from '../../data/products';
import { getStockMap, saveStockMap, STOCK_UPDATED_EVENT } from '../../utils/adminStorage';
import { db } from '../../firebase';
import { CheckCircle2, ChevronDown, Pencil, Trash2 } from 'lucide-react';

const getNowInputValue = () => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(
    now.getHours(),
  )}:${pad(now.getMinutes())}`;
};

const toInputDateTime = (value) => {
  if (!value) return '';
  let date = null;
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    date = value.toDate();
  } else {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      date = parsed;
    }
  }
  if (!date) return '';
  const pad = (val) => String(val).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

const toInputDate = (value) => {
  if (!value) return '';
  let date = null;
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    date = value.toDate();
  } else {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      date = parsed;
    }
  }
  if (!date) return '';
  const pad = (val) => String(val).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const UNCATEGORIZED_ID = 'uncategorized';

const toNonNegativeNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
};

const roundPrice = (value) => Math.round(value * 100) / 100;

const getProcurementItemTotalPrice = (item) => {
  if (!item || typeof item !== 'object') return null;
  const directTotal =
    toNonNegativeNumber(item.totalPrice)
    ?? toNonNegativeNumber(item.linePrice)
    ?? toNonNegativeNumber(item.price);
  if (directTotal !== null) return directTotal;

  const qty = Math.max(1, Number(item.qty) || 1);
  const unitPrice = toNonNegativeNumber(item.unitPrice);
  if (unitPrice === null) return null;
  return unitPrice * qty;
};

const getProcurementTotalPrice = (procurement) => {
  const items = Array.isArray(procurement?.items) ? procurement.items : [];
  let total = 0;
  let hasValue = false;
  items.forEach((item) => {
    const itemTotal = getProcurementItemTotalPrice(item);
    if (itemTotal === null) return;
    total += itemTotal;
    hasValue = true;
  });
  if (!hasValue) return null;
  return roundPrice(total);
};

export default function AdminInventory() {
  const { user, loading } = useAdminAuth();
  const { products: productsList } = useProducts();
  const [suppliers, setSuppliers] = useState([]);
  const [stock, setStock] = useState(() => getStockMap());
  const [scrapQtyByProduct, setScrapQtyByProduct] = useState({});
  const [scrappingProductId, setScrappingProductId] = useState(null);
  const [stockActionError, setStockActionError] = useState('');
  const [showActiveProductsOnly, setShowActiveProductsOnly] = useState(false);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const [selectedSupplierFilter, setSelectedSupplierFilter] = useState('all');
  const [showProcurementForm, setShowProcurementForm] = useState(false);
  const [showActiveProcurementsOnly, setShowActiveProcurementsOnly] = useState(true);
  const [editingProcurementId, setEditingProcurementId] = useState(null);
  const [expandedProcurementId, setExpandedProcurementId] = useState(null);
  const [procurements, setProcurements] = useState([]);
  const [inventoryAdjustments, setInventoryAdjustments] = useState([]);
  const [procurementForm, setProcurementForm] = useState({
    supplier: '',
    supplierId: '',
    purchaseAt: getNowInputValue(),
    pickupLocation: '',
    pickupAt: '',
    pickupAtEnd: '',
  });
  const [procurementItems, setProcurementItems] = useState([]);
  const [productQuery, setProductQuery] = useState('');
  const [procurementError, setProcurementError] = useState('');
  const [procurementLoading, setProcurementLoading] = useState(true);
  const [supplierSearchQuery, setSupplierSearchQuery] = useState('');
  const [isSupplierDropdownOpen, setIsSupplierDropdownOpen] = useState(false);
  const supplierDropdownRef = useRef(null);

  useEffect(() => {
    if (!user) {
      setProcurements([]);
      setProcurementLoading(false);
      return undefined;
    }
    setProcurementLoading(true);
    const q = query(collection(db, 'purchase'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const next = snapshot.docs.map((snap) => ({
          id: snap.id,
          ...snap.data(),
        }));
        setProcurements(next);
        setProcurementLoading(false);
      },
      () => {
        setProcurementLoading(false);
      },
    );
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user) {
      setSuppliers([]);
      return undefined;
    }
    const q = query(collection(db, 'suppliers'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const next = snapshot.docs.map((snap) => ({ id: snap.id, ...snap.data() }));
        setSuppliers(next);
      },
      () => {
        setSuppliers([]);
      },
    );
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user) {
      setInventoryAdjustments([]);
      return undefined;
    }
    const q = query(collection(db, 'inventoryAdjustments'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const next = snapshot.docs.map((snap) => ({ id: snap.id, ...snap.data() }));
        setInventoryAdjustments(next);
      },
      () => {
        setInventoryAdjustments([]);
      },
    );
    return () => unsub();
  }, [user]);

  useEffect(() => {
    setStock(getStockMap());
  }, [user]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const syncStock = (event) => {
      if (
        !event ||
        event.type === STOCK_UPDATED_EVENT ||
        event.key === null ||
        event.key === 'lumenverde-stock'
      ) {
        setStock(getStockMap());
      }
    };

    window.addEventListener(STOCK_UPDATED_EVENT, syncStock);
    window.addEventListener('storage', syncStock);
    window.addEventListener('focus', syncStock);
    return () => {
      window.removeEventListener(STOCK_UPDATED_EVENT, syncStock);
      window.removeEventListener('storage', syncStock);
      window.removeEventListener('focus', syncStock);
    };
  }, []);

  useEffect(() => {
    if (!isSupplierDropdownOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!supplierDropdownRef.current) return;
      if (!supplierDropdownRef.current.contains(event.target)) {
        setIsSupplierDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isSupplierDropdownOpen]);

  const handleProcurementChange = (event) => {
    const { name, value } = event.target;
    setProcurementForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddProcurementItem = (product) => {
    setProcurementItems((prev) => {
      const existing = prev.find((item) => item.productId === product.id);
      if (existing) {
        return prev.map((item) =>
          item.productId === product.id
            ? (() => {
              const nextQty = Math.max(1, Number(item.qty) || 1) + 1;
              const mode = item.priceMode || (item.unitPrice ? 'unit' : 'total');
              const totalParsed = toNonNegativeNumber(item.totalPrice);
              const unitParsed = toNonNegativeNumber(item.unitPrice);
              if (mode === 'unit' && unitParsed !== null) {
                return {
                  ...item,
                  qty: nextQty,
                  totalPrice: String(roundPrice(unitParsed * nextQty)),
                };
              }
              if (mode === 'total' && totalParsed !== null) {
                return {
                  ...item,
                  qty: nextQty,
                  unitPrice: String(roundPrice(totalParsed / nextQty)),
                };
              }
              return { ...item, qty: nextQty };
            })()
            : item,
        );
      }
      const plannedUnitPrice = toNonNegativeNumber(product?.plannedPurchasePrice);
      const normalizedPlannedUnitPrice =
        plannedUnitPrice !== null ? roundPrice(plannedUnitPrice) : null;
      return [
        ...prev,
        {
          productId: product.id,
          productName: product.name,
          qty: 1,
          totalPrice:
            normalizedPlannedUnitPrice !== null ? String(normalizedPlannedUnitPrice) : '',
          unitPrice:
            normalizedPlannedUnitPrice !== null ? String(normalizedPlannedUnitPrice) : '',
          priceMode: normalizedPlannedUnitPrice !== null ? 'unit' : 'total',
        },
      ];
    });
  };

  const handleProcurementQtyChange = (productId, value) => {
    const nextValue = Math.max(1, Number(value) || 1);
    setProcurementItems((prev) =>
      prev.map((item) => {
        if (item.productId !== productId) return item;
        const mode = item.priceMode || (item.unitPrice ? 'unit' : 'total');
        const totalParsed = toNonNegativeNumber(item.totalPrice);
        const unitParsed = toNonNegativeNumber(item.unitPrice);
        if (mode === 'unit' && unitParsed !== null) {
          return {
            ...item,
            qty: nextValue,
            totalPrice: String(roundPrice(unitParsed * nextValue)),
          };
        }
        if (mode === 'total' && totalParsed !== null) {
          return {
            ...item,
            qty: nextValue,
            unitPrice: String(roundPrice(totalParsed / nextValue)),
          };
        }
        return { ...item, qty: nextValue };
      }),
    );
  };

  const handleProcurementTotalPriceChange = (productId, value) => {
    setProcurementItems((prev) =>
      prev.map((item) =>
        item.productId === productId
          ? {
            ...item,
            totalPrice: value,
            unitPrice: (() => {
              const totalParsed = toNonNegativeNumber(value);
              if (totalParsed === null) return item.unitPrice ?? '';
              const qty = Math.max(1, Number(item.qty) || 1);
              return String(roundPrice(totalParsed / qty));
            })(),
            priceMode: 'total',
          }
          : item,
      ),
    );
  };

  const handleProcurementUnitPriceChange = (productId, value) => {
    setProcurementItems((prev) =>
      prev.map((item) =>
        item.productId === productId
          ? {
            ...item,
            unitPrice: value,
            totalPrice: (() => {
              const unitParsed = toNonNegativeNumber(value);
              if (unitParsed === null) return item.totalPrice ?? '';
              const qty = Math.max(1, Number(item.qty) || 1);
              return String(roundPrice(unitParsed * qty));
            })(),
            priceMode: 'unit',
          }
          : item,
      ),
    );
  };

  const handleRemoveProcurementItem = (productId) => {
    setProcurementItems((prev) => prev.filter((item) => item.productId !== productId));
  };

  const resetProcurementForm = () => {
    setProcurementForm({
      supplier: '',
      supplierId: '',
      purchaseAt: getNowInputValue(),
      pickupLocation: '',
      pickupAt: '',
      pickupAtEnd: '',
    });
    setProcurementItems([]);
    setProductQuery('');
    setProcurementError('');
    setSupplierSearchQuery('');
    setIsSupplierDropdownOpen(false);
  };

  const handleStartEditProcurement = (procurement) => {
    setShowProcurementForm(true);
    setProcurementError('');
    setEditingProcurementId(procurement.id);
    const items = Array.isArray(procurement.items) ? procurement.items : [];
    setProcurementItems(
      items.map((item) => ({
        productId: item.productId,
        productName: item.productName || getProductName(item.productId),
        qty: Math.max(1, Number(item.qty) || 1),
        totalPrice: getProcurementItemTotalPrice(item)?.toString() || '',
        unitPrice: (() => {
          const unitParsed = toNonNegativeNumber(item.unitPrice);
          if (unitParsed !== null) return String(unitParsed);
          const total = getProcurementItemTotalPrice(item);
          const qty = Math.max(1, Number(item.qty) || 1);
          return total !== null ? String(roundPrice(total / qty)) : '';
        })(),
        priceMode: toNonNegativeNumber(item.unitPrice) !== null ? 'unit' : 'total',
      })),
    );
    const normalizeSupplierName = (value) =>
      String(value || '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ');
    const rawSupplierName = String(procurement.supplier || '').trim();
    const exactSupplierMatches = activeSuppliers.filter(
      (supplier) => normalizeSupplierName(supplier?.name) === normalizeSupplierName(rawSupplierName),
    );
    const resolvedSupplierId = String(procurement.supplierId || '').trim()
      || (exactSupplierMatches.length === 1 ? exactSupplierMatches[0].id : '');

    setProcurementForm({
      supplier: rawSupplierName,
      supplierId: resolvedSupplierId,
      purchaseAt: toInputDateTime(procurement.purchaseAt) || getNowInputValue(),
      pickupLocation: procurement.pickupLocation || '',
      pickupAt: toInputDate(procurement.pickupAt),
      pickupAtEnd: toInputDate(procurement.pickupAtEnd),
    });
    setSupplierSearchQuery(procurement.supplier || '');
    setIsSupplierDropdownOpen(false);
    setProductQuery('');
  };

  const handleCancelProcurementEdit = () => {
    setEditingProcurementId(null);
    setShowProcurementForm(false);
    resetProcurementForm();
  };

  const handleAddProcurement = async (event) => {
    event.preventDefault();
    setProcurementError('');
    const supplier = procurementForm.supplier.trim();
    const supplierId = String(procurementForm.supplierId || '').trim();
    const purchaseAt = procurementForm.purchaseAt;
    const pickupLocation = procurementForm.pickupLocation.trim();
    const pickupAt = procurementForm.pickupAt;
    const pickupAtEnd = procurementForm.pickupAtEnd;

    if (!supplier || !purchaseAt || !pickupLocation) {
      setProcurementError('A szállító, a beszerzés ideje és az átvétel helye kötelező.');
      return;
    }
    const selectedSupplier = activeSuppliers.find((item) => item.id === supplierId);
    if (!selectedSupplier) {
      setProcurementError('A szállítót a legördülő listából válaszd ki.');
      return;
    }
    if (pickupAtEnd && !pickupAt) {
      setProcurementError('Az átvétel intervallumához add meg a kezdő dátumot.');
      return;
    }
    if (pickupAt && pickupAtEnd) {
      const startDate = new Date(`${pickupAt}T00:00:00`);
      const endDate = new Date(`${pickupAtEnd}T00:00:00`);
      if (endDate < startDate) {
        setProcurementError('Az átvétel vége nem lehet korábbi, mint a kezdete.');
        return;
      }
    }
    if (procurementItems.length === 0) {
      setProcurementError('Adj hozzá legalább egy terméket.');
      return;
    }

    const normalizedItems = procurementItems.map((item) => {
      const qty = Math.max(1, Number(item.qty) || 1);
      const totalParsed = toNonNegativeNumber(item.totalPrice);
      const unitParsed = toNonNegativeNumber(item.unitPrice);
      let totalPrice = totalParsed;
      let unitPrice = unitParsed;
      if (totalPrice === null && unitPrice === null) {
        return { ...item, qty, totalPrice: null, unitPrice: null };
      }
      if (totalPrice === null && unitPrice !== null) {
        totalPrice = roundPrice(unitPrice * qty);
      }
      if (unitPrice === null && totalPrice !== null) {
        unitPrice = roundPrice(totalPrice / qty);
      }
      return { ...item, qty, totalPrice, unitPrice };
    });

    if (normalizedItems.some((item) => item.totalPrice === null && item.unitPrice === null)) {
      setProcurementError('Minden tételnél add meg az összértéket vagy az egységárat.');
      return;
    }

    const toTimestamp = (value, isDateOnly = false) => {
      const source = isDateOnly ? `${value}T00:00:00` : value;
      const parsed = new Date(source);
      if (Number.isNaN(parsed.getTime())) return value;
      return Timestamp.fromDate(parsed);
    };

    const payload = {
      supplier: String(selectedSupplier?.name || supplier),
      supplierId: selectedSupplier.id,
      items: normalizedItems.map((item) => ({
        productId: item.productId,
        productName: item.productName,
        qty: item.qty,
        totalPrice: item.totalPrice,
        unitPrice: item.unitPrice,
      })),
      purchaseAt: toTimestamp(purchaseAt),
      pickupLocation,
      pickupAt: pickupAt ? toTimestamp(pickupAt, true) : null,
      pickupAtEnd: pickupAtEnd ? toTimestamp(pickupAtEnd, true) : null,
      updatedAt: serverTimestamp(),
    };

    try {
      if (editingProcurementId) {
        await updateDoc(doc(db, 'purchase', editingProcurementId), payload);
      } else {
        await addDoc(collection(db, 'purchase'), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      setEditingProcurementId(null);
      setShowProcurementForm(false);
      resetProcurementForm();
    } catch {
      setProcurementError('Nem sikerült elmenteni a beszerzést.');
    }
  };

  const handleDeleteProcurement = async (id) => {
    if (!window.confirm('Biztosan törlöd ezt a beszerzést?')) return;
    try {
      await deleteDoc(doc(db, 'purchase', id));
    } catch {
      setProcurementError('Nem sikerült törölni a beszerzést.');
    }
  };

  const handleReceiveProcurement = async (procurement) => {
    if (procurement.receivedAt) return;
    try {
      await updateDoc(doc(db, 'purchase', procurement.id), {
        receivedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      const nextStock = { ...stock };
      const items = Array.isArray(procurement.items) ? procurement.items : [];
      items.forEach((item) => {
        if (!item?.productId) return;
        const qty = Math.max(1, Number(item.qty) || 1);
        nextStock[item.productId] = (Number(nextStock[item.productId]) || 0) + qty;
      });
      setStock(nextStock);
      saveStockMap(nextStock);
    } catch {
      setProcurementError('Nem sikerült átvenni a beszerzést.');
    }
  };

  const handleScrapQtyChange = (productId, value) => {
    setScrapQtyByProduct((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const handleScrapProduct = async (product) => {
    const productId = product?.id;
    if (!productId || scrappingProductId) return;
    const parsedQty = Math.floor(Number(scrapQtyByProduct[productId]));
    if (!Number.isFinite(parsedQty) || parsedQty <= 0) {
      setStockActionError('A selejtezéshez pozitív darabszámot adj meg.');
      return;
    }

    const currentStock = Number(stock[productId]) || 0;
    if (parsedQty > currentStock) {
      setStockActionError(`A selejtezés nem lehet több, mint a készlet (${currentStock} db).`);
      return;
    }

    setScrappingProductId(productId);
    try {
      const currentInventoryValue = Number(inventoryValueByProduct[productId]) || 0;
      const unitPrice = currentStock > 0 ? currentInventoryValue / currentStock : 0;
      const scrapValue = Math.max(0, unitPrice * parsedQty);

      await addDoc(collection(db, 'inventoryAdjustments'), {
        type: 'scrap',
        productId,
        productName: product?.name || getProductName(productId),
        qty: parsedQty,
        unitPrice,
        amount: scrapValue,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const nextStock = {
        ...stock,
        [productId]: currentStock - parsedQty,
      };
      setStock(nextStock);
      saveStockMap(nextStock);
      setScrapQtyByProduct((prev) => ({
        ...prev,
        [productId]: '',
      }));
      setStockActionError('');
    } catch {
      setStockActionError('Nem sikerült elvégezni a selejtezést.');
    } finally {
      setScrappingProductId(null);
    }
  };

  const toggleProcurementRow = (id) => {
    setExpandedProcurementId((prev) => (prev === id ? null : id));
  };

  const formatDateTime = (value) => {
    if (!value) return '—';
    if (typeof value === 'object' && typeof value.toDate === 'function') {
      return value.toDate().toLocaleString('hu-HU');
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString('hu-HU');
  };

  const formatDateOnly = (value) => {
    if (!value) return '—';
    if (typeof value === 'object' && typeof value.toDate === 'function') {
      return value.toDate().toLocaleDateString('hu-HU');
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('hu-HU');
  };

  const formatDateRange = (start, end) => {
    const hasStart = Boolean(start);
    const hasEnd = Boolean(end);
    if (!hasStart && !hasEnd) return '—';
    const startLabel = hasStart ? formatDateOnly(start) : '';
    const endLabel = hasEnd ? formatDateOnly(end) : '';
    if (hasStart && hasEnd) {
      return startLabel === endLabel ? startLabel : `${startLabel} – ${endLabel}`;
    }
    return hasStart ? startLabel : endLabel;
  };

  const activeProcurements = useMemo(
    () => procurements.filter((item) => !item?.receivedAt),
    [procurements],
  );

  const visibleProcurements = useMemo(
    () => (showActiveProcurementsOnly ? activeProcurements : procurements),
    [activeProcurements, procurements, showActiveProcurementsOnly],
  );

  const inTransit = useMemo(() => {
    const totals = {};
    activeProcurements.forEach((procurement) => {
      const items = Array.isArray(procurement.items) ? procurement.items : [];
      items.forEach((item) => {
        if (!item?.productId) return;
        const qty = Math.max(1, Number(item.qty) || 1);
        totals[item.productId] = (Number(totals[item.productId]) || 0) + qty;
      });
    });
    return totals;
  }, [activeProcurements]);

  const receivedTotalValueByProduct = useMemo(() => {
    const totals = {};
    procurements.forEach((procurement) => {
      if (!procurement?.receivedAt) return;
      const items = Array.isArray(procurement.items) ? procurement.items : [];
      items.forEach((item) => {
        if (!item?.productId) return;
        const totalPrice = getProcurementItemTotalPrice(item);
        if (totalPrice === null) return;
        totals[item.productId] = (Number(totals[item.productId]) || 0) + totalPrice;
      });
    });
    return totals;
  }, [procurements]);

  const scrapedValueByProduct = useMemo(() => {
    const totals = {};
    inventoryAdjustments.forEach((entry) => {
      if (entry?.type !== 'scrap') return;
      const productId = entry?.productId;
      if (!productId) return;
      const amount = toNonNegativeNumber(entry?.amount);
      if (amount === null) return;
      totals[productId] = (Number(totals[productId]) || 0) + amount;
    });
    return totals;
  }, [inventoryAdjustments]);

  const packedValueByProduct = useMemo(() => {
    const totals = {};
    inventoryAdjustments.forEach((entry) => {
      if (entry?.type !== 'order_pack') return;
      const productId = entry?.productId;
      if (!productId) return;
      const amount = toNonNegativeNumber(entry?.amount);
      if (amount === null) return;
      totals[productId] = (Number(totals[productId]) || 0) + amount;
    });
    return totals;
  }, [inventoryAdjustments]);

  const unpackedValueByProduct = useMemo(() => {
    const totals = {};
    inventoryAdjustments.forEach((entry) => {
      if (entry?.type !== 'order_unpack') return;
      const productId = entry?.productId;
      if (!productId) return;
      const amount = toNonNegativeNumber(entry?.amount);
      if (amount === null) return;
      totals[productId] = (Number(totals[productId]) || 0) + amount;
    });
    return totals;
  }, [inventoryAdjustments]);

  const inventoryValueByProduct = useMemo(() => {
    const next = {};
    const ids = new Set([
      ...Object.keys(receivedTotalValueByProduct),
      ...Object.keys(scrapedValueByProduct),
      ...Object.keys(packedValueByProduct),
      ...Object.keys(unpackedValueByProduct),
    ]);
    ids.forEach((productId) => {
      const receivedValue = Number(receivedTotalValueByProduct[productId]) || 0;
      const scrapedValue = Number(scrapedValueByProduct[productId]) || 0;
      const packedValue = Number(packedValueByProduct[productId]) || 0;
      const unpackedValue = Number(unpackedValueByProduct[productId]) || 0;
      next[productId] = Math.max(0, receivedValue - scrapedValue - packedValue + unpackedValue);
    });
    return next;
  }, [
    receivedTotalValueByProduct,
    scrapedValueByProduct,
    packedValueByProduct,
    unpackedValueByProduct,
  ]);

  const getProductName = (id) => {
    const found = productsList.find((product) => product.id === id);
    return found?.name || id;
  };

  const formatTotalPrice = (value) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return null;
    if (value < 0) return null;
    return `${value.toLocaleString('hu-HU')} Ft`;
  };

  const activeSuppliers = useMemo(
    () => suppliers.filter((supplier) => supplier?.isActive !== false),
    [suppliers],
  );

  const activeSupplierById = useMemo(
    () => new Map(activeSuppliers.map((supplier) => [supplier.id, supplier])),
    [activeSuppliers],
  );

  const supplierProductIds = useMemo(() => {
    const normalizeName = (value) =>
      String(value || '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ');
    const selectedSupplierId = String(procurementForm.supplierId || '').trim();
    const selectedById = selectedSupplierId ? activeSupplierById.get(selectedSupplierId) : null;
    let matchedSuppliers = [];

    if (selectedById) {
      matchedSuppliers = [selectedById];
    } else {
      const supplierQuery = normalizeName(procurementForm.supplier);
      if (!supplierQuery) return null;
      const exactMatches = activeSuppliers.filter(
        (supplier) => normalizeName(supplier?.name) === supplierQuery,
      );
      matchedSuppliers = exactMatches.length
        ? exactMatches
        : activeSuppliers.filter((supplier) => normalizeName(supplier?.name).includes(supplierQuery));
    }

    if (!matchedSuppliers.length) return new Set();

    const productIdByName = new Map(
      productsList.map((product) => [normalizeName(product?.name), product.id]),
    );
    const ids = new Set();
    matchedSuppliers.forEach((supplier) => {
      const items = Array.isArray(supplier.products) ? supplier.products : [];
      items.forEach((item) => {
        const directId = String(item?.productId || '').trim();
        const nameId = productIdByName.get(normalizeName(item?.productName));
        const resolvedId = directId || nameId;
        if (resolvedId) ids.add(resolvedId);
      });
    });
    return ids;
  }, [procurementForm.supplierId, procurementForm.supplier, activeSupplierById, activeSuppliers, productsList]);

  const supplierOptions = useMemo(() => {
    const names = new Set();
    activeSuppliers.forEach((supplier) => {
      if (supplier?.name) names.add(supplier.name);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'hu'));
  }, [activeSuppliers]);

  const procurementSupplierOptions = useMemo(() => {
    const options = activeSuppliers
      .map((supplier) => ({
        id: supplier.id,
        name: String(supplier?.name || '').trim(),
      }))
      .filter((option) => option.name)
      .sort((a, b) => a.name.localeCompare(b.name, 'hu'));

    const currentSupplierName = procurementForm.supplier.trim();
    const currentSupplierId = String(procurementForm.supplierId || '').trim();

    if (
      currentSupplierName
      && !options.some((option) => option.id === currentSupplierId || option.name === currentSupplierName)
    ) {
      options.unshift({
        id: '',
        name: currentSupplierName,
        isLegacy: true,
      });
    }
    return options;
  }, [activeSuppliers, procurementForm.supplier, procurementForm.supplierId]);

  const filteredProcurementSupplierOptions = useMemo(() => {
    const normalizedQuery = supplierSearchQuery.trim().toLowerCase();
    if (!normalizedQuery) return procurementSupplierOptions;
    return procurementSupplierOptions.filter((option) =>
      option.name.toLowerCase().includes(normalizedQuery),
    );
  }, [supplierSearchQuery, procurementSupplierOptions]);

  const handleSupplierDropdownToggle = () => {
    if (isSupplierDropdownOpen) {
      setIsSupplierDropdownOpen(false);
      return;
    }
    setSupplierSearchQuery(procurementForm.supplier || '');
    setIsSupplierDropdownOpen(true);
  };

  const handleSelectProcurementSupplier = (option) => {
    setProcurementForm((prev) => ({
      ...prev,
      supplier: option.name,
      supplierId: option.isLegacy ? '' : option.id,
    }));
    setSupplierSearchQuery(option.name);
    setIsSupplierDropdownOpen(false);
    setProcurementError('');
  };

  const supplierFilterProductIds = useMemo(() => {
    if (selectedSupplierFilter === 'all') return null;
    const matchedSuppliers = activeSuppliers.filter(
      (supplier) => supplier?.name && supplier.name === selectedSupplierFilter,
    );
    const ids = new Set();
    matchedSuppliers.forEach((supplier) => {
      const items = Array.isArray(supplier.products) ? supplier.products : [];
      items.forEach((item) => {
        if (item?.productId) ids.add(item.productId);
      });
    });
    return ids;
  }, [selectedSupplierFilter, activeSuppliers]);

  useEffect(() => {
    if (selectedSupplierFilter === 'all') return;
    if (!supplierOptions.includes(selectedSupplierFilter)) {
      setSelectedSupplierFilter('all');
    }
  }, [selectedSupplierFilter, supplierOptions]);

  const categoryFilterOptions = useMemo(() => {
    const used = new Set();
    productsList.forEach((product) => {
      used.add(product.category || UNCATEGORIZED_ID);
    });
    const options = [{ value: 'all', label: 'Összes kategória' }];
    if (used.has(UNCATEGORIZED_ID)) {
      options.push({ value: UNCATEGORIZED_ID, label: 'Nincs kategória' });
    }
    categories.forEach((category) => {
      if (used.has(category.id)) {
        options.push({ value: category.id, label: category.name });
      }
    });
    Array.from(used)
      .filter(
        (value) => value !== UNCATEGORIZED_ID && !categories.some((category) => category.id === value),
      )
      .sort((a, b) => a.localeCompare(b, 'hu'))
      .forEach((value) => options.push({ value, label: value }));
    return options;
  }, [productsList]);

  const filteredProducts = useMemo(() => {
    const normalizedQuery = productQuery.trim().toLowerCase();
    return productsList.filter((product) => {
      if (supplierProductIds && !supplierProductIds.has(product.id)) return false;
      if (!normalizedQuery) return true;
      const haystack = `${product.name} ${product.id}`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [productQuery, productsList, supplierProductIds]);

  const filteredStockProducts = useMemo(() => {
    return productsList.filter((product) => {
      if (showActiveProductsOnly && product.isActive === false) return false;
      const categoryValue = product.category || UNCATEGORIZED_ID;
      if (selectedCategoryFilter !== 'all' && categoryValue !== selectedCategoryFilter) return false;
      if (supplierFilterProductIds && !supplierFilterProductIds.has(product.id)) return false;
      return true;
    });
  }, [
    productsList,
    showActiveProductsOnly,
    selectedCategoryFilter,
    supplierFilterProductIds,
  ]);

  if (loading) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-zinga">
            <div className="rounded-3xl border border-[#0F3D2C] p-8 md:p-10 bg-card/60">
              <p className="text-sm text-muted-foreground">Betöltés...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-zinga">
            <h1 className="font-display text-3xl md:text-4xl font-bold mb-6">Admin / Beszerzés</h1>
            <AdminLoginCard description="A készlet nyilvántartásához jelentkezz be Google fiókkal." />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="section-padding">
        <div className="container-zinga">
          <AdminHeader title="Admin / Beszerzés" />
          <AdminNav />

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <div className="rounded-3xl border border-border/70 bg-card p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h2 className="font-display text-xl font-semibold">Készlet</h2>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showActiveProductsOnly}
                      onChange={(event) => setShowActiveProductsOnly(event.target.checked)}
                    />
                    Csak aktív termékek
                  </label>
                </div>
              </div>
              <div className="mb-4 grid gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium mb-1">Kategória</label>
                  <select
                    value={selectedCategoryFilter}
                    onChange={(event) => setSelectedCategoryFilter(event.target.value)}
                    className="input-zinga"
                  >
                    {categoryFilterOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Szállító</label>
                  <select
                    value={selectedSupplierFilter}
                    onChange={(event) => setSelectedSupplierFilter(event.target.value)}
                    className="input-zinga"
                  >
                    <option value="all">Összes szállító</option>
                    {supplierOptions.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {stockActionError && (
                <p className="mb-3 text-xs text-destructive">{stockActionError}</p>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border/70">
                      <th className="py-3 pr-4">Termék</th>
                      <th className="py-3 pr-4">Raktárkészlet</th>
                      <th className="py-3 pr-4">Úton lévő</th>
                      <th className="py-3 pr-0">Selejtezés</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStockProducts.map((product) => (
                      <tr key={product.id} className="border-b border-border/40 last:border-0">
                        <td className="py-3 pr-4">
                          <p className="font-medium">{product.name}</p>
                          <p className="text-xs text-muted-foreground">ID: {product.id}</p>
                        </td>
                        <td className="py-3 pr-4">
                          <span className="text-sm text-foreground/80">{stock[product.id] ?? 0}</span>
                        </td>
                        <td className="py-3 pr-4">
                          <span className="text-sm text-foreground/80">{inTransit[product.id] ?? 0}</span>
                        </td>
                        <td className="py-3 pr-0">
                          <div className="flex items-center justify-end gap-2">
                            <input
                              type="number"
                              min="1"
                              value={scrapQtyByProduct[product.id] ?? ''}
                              onChange={(event) =>
                                handleScrapQtyChange(product.id, event.target.value)
                              }
                              placeholder="db"
                              className="input-zinga w-20 text-xs py-1.5"
                            />
                            <button
                              type="button"
                              onClick={() => handleScrapProduct(product)}
                              className="btn-secondary text-xs px-3 py-1.5"
                              disabled={
                                scrappingProductId === product.id ||
                                (Number(stock[product.id]) || 0) <= 0
                              }
                            >
                              Selejtez
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-3xl border border-border/70 bg-card p-6">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div>
                  <h2 className="font-display text-xl font-semibold">Beszerzések</h2>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (showProcurementForm) {
                      handleCancelProcurementEdit();
                      return;
                    }
                    setEditingProcurementId(null);
                    resetProcurementForm();
                    setShowProcurementForm(true);
                  }}
                  className="btn-secondary text-sm"
                >
                  {showProcurementForm ? 'Bezárás' : '+ Új beszerzés'}
                </button>
              </div>
              {showProcurementForm ? (
                <form onSubmit={handleAddProcurement} className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-xs font-medium mb-1">Szállító</label>
                    <div ref={supplierDropdownRef} className="relative">
                      <button
                        type="button"
                        onClick={handleSupplierDropdownToggle}
                        className="input-zinga w-full flex items-center justify-between text-left"
                      >
                        <span className={procurementForm.supplier ? '' : 'text-muted-foreground'}>
                          {procurementForm.supplier || 'Válassz szállítót'}
                        </span>
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      </button>
                      {isSupplierDropdownOpen ? (
                        <div className="absolute z-20 mt-2 w-full rounded-2xl border border-border/70 bg-card p-2 shadow-lg">
                          <input
                            type="text"
                            value={supplierSearchQuery}
                            onChange={(event) => setSupplierSearchQuery(event.target.value)}
                            className="input-zinga"
                            placeholder="Keresés..."
                            autoFocus
                          />
                          <div className="mt-2 max-h-52 overflow-y-auto space-y-1 pr-1">
                            {filteredProcurementSupplierOptions.length === 0 ? (
                              <p className="px-2 py-2 text-xs text-muted-foreground">
                                Nincs találat.
                              </p>
                            ) : (
                              filteredProcurementSupplierOptions.map((option) => (
                                <button
                                  key={`${option.id || 'legacy'}-${option.name}`}
                                  type="button"
                                  onClick={() => handleSelectProcurementSupplier(option)}
                                  className={`w-full rounded-xl px-3 py-2 text-left text-sm transition-colors hover:bg-secondary/50 ${
                                    (option.id
                                      ? option.id === procurementForm.supplierId
                                      : option.name === procurementForm.supplier)
                                      ? 'bg-secondary/60'
                                      : ''
                                  }`}
                                >
                                  {option.name}
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Beszerzés ideje</label>
                    <input
                      type="datetime-local"
                      name="purchaseAt"
                      value={procurementForm.purchaseAt}
                      onChange={handleProcurementChange}
                      className="input-zinga"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium mb-1">Beszerzett termékek</label>
                    <div className="rounded-2xl border border-border/70 bg-secondary/20 p-4">
                      <div>
                        <label htmlFor="procurement-search" className="block text-xs font-medium mb-2 text-muted-foreground">
                          Keresés az elérhető termékek között
                        </label>
                        <input
                          id="procurement-search"
                          type="text"
                          value={productQuery}
                          onChange={(event) => setProductQuery(event.target.value)}
                          className="input-zinga"
                          placeholder="Kezdj el gépelni..."
                        />
                      </div>
                      <div className="mt-4 max-h-44 overflow-y-auto space-y-2 pr-1">
                        {filteredProducts.length === 0 ? (
                          <p className="text-xs text-muted-foreground">Nincs találat.</p>
                        ) : (
                          filteredProducts.map((product) => {
                            const isSelected = procurementItems.some((item) => item.productId === product.id);
                            return (
                              <div
                                key={product.id}
                                className="flex items-center justify-between gap-3 rounded-xl border border-border/50 bg-background px-3 py-2"
                              >
                                <div>
                                  <p className="text-sm font-medium">{product.name}</p>
                                  <p className="text-xs text-muted-foreground">ID: {product.id}</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleAddProcurementItem(product)}
                                  className="btn-secondary text-xs"
                                >
                                  {isSelected ? '+ Még egy' : '+ Hozzáad'}
                                </button>
                              </div>
                            );
                          })
                        )}
                      </div>

                      <div className="mt-4">
                        <p className="text-xs font-medium mb-2 text-muted-foreground">Kiválasztott termékek</p>
                        <p className="mb-2 text-[11px] text-muted-foreground">
                          Add meg az összértéket vagy az egységárat. A másik mező automatikusan
                          kiszámolódik a mennyiség alapján.
                        </p>
                        {procurementItems.length === 0 ? (
                          <p className="text-xs text-muted-foreground">Még nincs kiválasztott termék.</p>
                        ) : (
                          <div className="space-y-2">
                            {procurementItems.map((item) => (
                              <div
                                key={item.productId}
                                className="flex items-center gap-4 rounded-xl border border-border/50 bg-background px-3 py-2"
                              >
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium truncate" title={item.productName}>
                                    {item.productName}
                                  </p>
                                  <p className="text-xs text-muted-foreground">ID: {item.productId}</p>
                                </div>
                                <div className="flex items-end gap-3 flex-nowrap">
                                  <label className="flex flex-col items-start gap-1 text-[11px] text-muted-foreground">
                                    <span>DB</span>
                                    <input
                                      type="number"
                                      min="1"
                                      value={item.qty}
                                      onChange={(event) => handleProcurementQtyChange(item.productId, event.target.value)}
                                      className="input-zinga w-20"
                                    />
                                  </label>
                                  <label className="flex flex-col items-start gap-1 text-[11px] text-muted-foreground">
                                    <span>Összérték (Ft)</span>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={item.totalPrice ?? ''}
                                      onChange={(event) =>
                                        handleProcurementTotalPriceChange(
                                          item.productId,
                                          event.target.value,
                                        )
                                      }
                                      className="input-zinga w-28"
                                      placeholder="Összesen"
                                    />
                                  </label>
                                  <label className="flex flex-col items-start gap-1 text-[11px] text-muted-foreground">
                                    <span>Egységár (Ft)</span>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={item.unitPrice ?? ''}
                                      onChange={(event) =>
                                        handleProcurementUnitPriceChange(
                                          item.productId,
                                          event.target.value,
                                        )
                                      }
                                      className="input-zinga w-[4.2rem] appearance-none [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                      placeholder="Egység"
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveProcurementItem(item.productId)}
                                    className="btn-secondary text-xs text-destructive px-2 py-1.5"
                                    title="Törlés"
                                    aria-label="Törlés"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Átvétel helye</label>
                    <input
                      name="pickupLocation"
                      value={procurementForm.pickupLocation}
                      onChange={handleProcurementChange}
                      className="input-zinga"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Átvétel kezdete</label>
                    <input
                      type="date"
                      name="pickupAt"
                      value={procurementForm.pickupAt}
                      onChange={handleProcurementChange}
                      className="input-zinga"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Átvétel vége</label>
                    <input
                      type="date"
                      name="pickupAtEnd"
                      value={procurementForm.pickupAtEnd}
                      onChange={handleProcurementChange}
                      className="input-zinga"
                    />
                  </div>
                  <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-3">
                    {procurementError && (
                      <span className="text-xs text-destructive">{procurementError}</span>
                    )}
                    <div className="flex flex-wrap items-center gap-3">
                      {editingProcurementId ? (
                        <button
                          type="button"
                          onClick={handleCancelProcurementEdit}
                          className="btn-secondary text-sm"
                        >
                          Mégse
                        </button>
                      ) : null}
                      <button type="submit" className="btn-primary text-sm">
                        {editingProcurementId ? 'Mentés' : '+ Beszerzés hozzáadása'}
                      </button>
                    </div>
                  </div>
                </form>
              ) : null}

              <div className="mt-6">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <h3 className="text-sm font-semibold">Aktuális beszerzések</h3>
                  <label className="flex items-center gap-2 text-xs cursor-pointer text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={showActiveProcurementsOnly}
                      onChange={(event) => setShowActiveProcurementsOnly(event.target.checked)}
                    />
                    Csak aktív
                  </label>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border/70">
                      <th className="py-3 pr-4">Szállító</th>
                      <th className="py-3 pr-4">Átvétel</th>
                      <th className="py-3 pr-4">Összérték</th>
                      <th className="py-3 pr-0 text-right">Művelet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {procurementLoading ? (
                      <tr>
                        <td className="py-4 text-sm text-muted-foreground" colSpan={4}>
                          Betöltés...
                        </td>
                      </tr>
                    ) : visibleProcurements.length === 0 ? (
                      <tr>
                        <td className="py-4 text-sm text-muted-foreground" colSpan={4}>
                          Nincs rögzített beszerzés.
                        </td>
                      </tr>
                    ) : (
                      visibleProcurements.map((item) => (
                        <React.Fragment key={item.id}>
                          <tr
                            className="border-b border-border/40 last:border-0 cursor-pointer hover:bg-secondary/20"
                            onClick={() => toggleProcurementRow(item.id)}
                          >
                            <td className="py-3 pr-4 font-medium">{item.supplier || '—'}</td>
                            <td className="py-3 pr-4">
                              <div className="text-sm">{item.pickupLocation || '—'}</div>
                              <div className="text-xs text-muted-foreground">
                                {formatDateRange(item.pickupAt, item.pickupAtEnd)}
                              </div>
                            </td>
                            <td className="py-3 pr-4">
                              <span className="text-sm text-foreground/90">
                                {formatTotalPrice(getProcurementTotalPrice(item)) || '—'}
                              </span>
                            </td>
                            <td className="py-3 pr-0 text-right">
                              <div className="flex flex-wrap justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleStartEditProcurement(item);
                                  }}
                                  className="btn-secondary text-xs px-2"
                                  disabled={Boolean(item.receivedAt)}
                                  title="Szerkesztés"
                                  aria-label="Szerkesztés"
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleReceiveProcurement(item);
                                  }}
                                  className={`btn-secondary text-xs px-2 ${
                                    item.receivedAt
                                      ? '!bg-emerald-700 !text-white hover:!bg-emerald-700'
                                      : ''
                                  }`}
                                  disabled={Boolean(item.receivedAt)}
                                  title={item.receivedAt ? 'Átvéve' : 'Átvett'}
                                  aria-label={item.receivedAt ? 'Átvéve' : 'Átvett'}
                                >
                                  <CheckCircle2 size={14} />
                                </button>
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDeleteProcurement(item.id);
                                  }}
                                  className="btn-secondary text-xs text-destructive px-2"
                                  title="Törlés"
                                  aria-label="Törlés"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {expandedProcurementId === item.id ? (
                            <tr className="border-b border-border/40 last:border-0 bg-secondary/10">
                              <td className="pb-4 pl-4 pr-4 pt-0" colSpan={4}>
                                <div className="grid gap-4 md:grid-cols-2">
                                  <div>
                                    <p className="text-xs text-muted-foreground mb-1">
                                      Beszerzés ideje
                                    </p>
                                    <p className="text-sm font-medium">
                                      {formatDateTime(item.purchaseAt)}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-muted-foreground mb-1">Termékek</p>
                                    {Array.isArray(item.items) ? (
                                      <div className="space-y-1 text-sm">
                                        {item.items.map((entry, index) => {
                                          const label =
                                            entry.productName || getProductName(entry.productId);
                                          const qty = Math.max(1, Number(entry.qty) || 1);
                                          const price = formatTotalPrice(
                                            getProcurementItemTotalPrice(entry),
                                          );
                                          return (
                                            <div key={`${entry.productId || 'item'}-${index}`}>
                                              {qty}x {label}
                                              {price ? ` (${price} összesen)` : ''}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <p className="text-sm">{item.items || '—'}</p>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
